const TZ_ABBR_TO_APPROVED = {
  EDT: "EST",
  PDT: "PST",
  CDT: "CST",
  MDT: "MST",
  AST: "EST",
  ADT: "EST",
  NST: "EST",
  NDT: "EST",
  HST: "PST",
  AKST: "PST",
  AKDT: "PST",
  GMT: "EST",
  UTC: "EST",
  BST: "EST",
  WET: "EST",
  WEST: "EST",
  CET: "EST",
  CEST: "EST",
  EET: "EST",
  EEST: "EST",
  WAT: "EST",
  CAT: "EST",
  EAT: "EST",
  JST: "IST",
  HKT: "IST",
  SGT: "IST",
  PKT: "IST",
  NPT: "IST",
  BTT: "IST",
  AEST: "IST",
  AEDT: "IST",
  ACST: "IST",
  AWST: "IST",
  NZST: "IST",
  NZDT: "IST",
};

const APPROVED_TZ = ["IST", "EST", "CST", "MST", "PST"];

const TZ_OFFSETS = {
  IST: 330,
  EST: -300,
  EDT: -240,
  CST: -360,
  CDT: -300,
  MST: -420,
  MDT: -360,
  PST: -480,
  PDT: -420,
  GMT: 0,
  UTC: 0,
  BST: 60,
  WET: 0,
  WEST: 60,
  CET: 60,
  CEST: 120,
  EET: 120,
  EEST: 180,
  JST: 540,
  HKT: 480,
  SGT: 480,
  PKT: 300,
  NPT: 345,
  AEST: 600,
  AEDT: 660,
  ACST: 570,
  AWST: 480,
  NZST: 720,
  NZDT: 780,
  AST: -240,
  ADT: -180,
  HST: -600,
  AKST: -540,
  AKDT: -480,
};

// Offset (minutes east of UTC) → canonical abbreviation.
// Used only when toLocaleTimeString returns a GMT±offset string.
const OFFSET_TO_ABBR = {
  "-720": "IDLW", "-660": "SST",  "-600": "HST",  "-570": "MART",
  "-540": "AKST", "-480": "PST",  "-420": "MST",  "-360": "CST",
  "-300": "EST",  "-240": "AST",  "-210": "NST",  "-180": "BRT",
  "-120": "FNT",  "-60":  "CVT",  "0":    "GMT",  "60":   "CET",
  "120":  "EET",  "180":  "EAT",  "210":  "IRST", "240":  "GST",
  "270":  "PKT",  "300":  "PKT",  "330":  "IST",  "345":  "NPT",
  "360":  "BST",  "390":  "MMT",  "420":  "ICT",  "480":  "SGT",
  "540":  "JST",  "570":  "ACST", "600":  "AEST", "660":  "AEDT",
  "720":  "NZST", "765":  "CHAST","780":  "NZDT", "840":  "LINT",
};

/**
 * Returns the account's actual local timezone abbreviation for use in
 * payload timestamps. Never converts to GMT/UTC, never defaults to IST
 * unless the system is genuinely in IST (UTC+5:30).
 *
 * Strategy:
 *   1. Ask Intl.DateTimeFormat for the short abbreviation of the system IANA zone.
 *   2. If the browser returns a clean named abbreviation (e.g. "IST", "CET",
 *      "EST"), use it as-is.
 *   3. If the browser returns "GMT+5:30" / "UTC-5:00" style, derive the
 *      named abbreviation from OFFSET_TO_ABBR using the actual UTC offset.
 */
export function getPayloadTzAbbr() {
  const now = new Date();
  const iana = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const abbr =
    new Intl.DateTimeFormat("en-US", { timeZone: iana, timeZoneName: "short" })
      .formatToParts(now)
      .find((p) => p.type === "timeZoneName")?.value ?? "";
  // Named abbreviation (no +/- offset component) — use directly
  if (/^[A-Z]{2,5}$/.test(abbr)) return abbr;
  // GMT±HH:MM / UTC±HH:MM from browser — resolve via actual UTC offset
  const offsetMin = String(-now.getTimezoneOffset());
  return OFFSET_TO_ABBR[offsetMin] ?? abbr;
}

/**
 * Maps any timezone abbreviation or GMT/UTC offset string
 * (e.g. "GMT+5:30", "UTC-5") to one of: IST, EST, CST, MST, PST.
 * Used only for display normalisation in convertEntryToSystemTz.
 */
export function resolveApprovedAbbr(raw) {
  if (APPROVED_TZ.includes(raw)) return raw;
  if (TZ_ABBR_TO_APPROVED[raw]) return TZ_ABBR_TO_APPROVED[raw];
  const gmtMatch = raw.match(/^(?:GMT|UTC)([+-])(\d{1,2})(?::(\d{2}))?$/);
  const minutes = gmtMatch
    ? (gmtMatch[1] === "+" ? 1 : -1) *
      (parseInt(gmtMatch[2], 10) * 60 + parseInt(gmtMatch[3] || "0", 10))
    : -new Date().getTimezoneOffset();
  if (minutes >= 270 && minutes <= 390) return "IST";
  if (minutes >= -330 && minutes <= -270) return "EST";
  if (minutes >= -390 && minutes <= -331) return "CST";
  if (minutes >= -450 && minutes <= -391) return "MST";
  if (minutes >= -510 && minutes <= -451) return "PST";
  return "IST";
}

function parseOffsetMinutes(tzStr) {
  if (TZ_OFFSETS[tzStr] !== undefined) return TZ_OFFSETS[tzStr];
  const m = tzStr.match(/^[A-Z]*([+-])(\d{1,2})(?::(\d{2}))?$/);
  if (m) {
    const sign = m[1] === "+" ? 1 : -1;
    return sign * (parseInt(m[2], 10) * 60 + parseInt(m[3] || "0", 10));
  }
  return null;
}

/**
 * Converts the embedded timestamp in a listOfNotes entry from its
 * original timezone (any abbreviation or GMT/UTC offset) to the
 * user's current system timezone. Returns the entry unchanged if
 * it doesn't match the expected note format.
 */
export function convertEntryToSystemTz(entry) {
  if (!entry) return entry;
  const match = entry.match(
    /^(.*?) - (\d{2}-\d{2}-\d{4}) (\d{1,2}:\d{2}) (AM|PM) ([A-Z]{2,5}(?:[+-]\d{1,2}(?::\d{2})?)?) - ([\s\S]*)$/
  );
  if (!match) return entry;
  const [, user, datePart, hhmm, ampm, tzStr, noteText] = match;
  const srcOffset = parseOffsetMinutes(tzStr);
  if (srcOffset === null) return entry;
  const [mm, dd, yyyy] = datePart.split("-").map(Number);
  let [h, mn] = hhmm.split(":").map(Number);
  if (ampm === "PM" && h !== 12) h += 12;
  if (ampm === "AM" && h === 12) h = 0;
  const utcMs = Date.UTC(yyyy, mm - 1, dd, h, mn) - srcOffset * 60000;
  const d = new Date(utcMs);
  const systemTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const rawAbbr = d
    .toLocaleTimeString("en-US", { timeZoneName: "short" })
    .split(" ")
    .pop();
  const outTzAbbr = resolveApprovedAbbr(rawAbbr);
  const outDate = d
    .toLocaleDateString("en-US", {
      timeZone: systemTz,
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    })
    .replace(/\//g, "-");
  const outTime = d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: systemTz,
  });
  return `${user} - ${outDate} ${outTime} ${outTzAbbr} - ${noteText}`;
}
